import type {Instant, UUID} from './common'
import {EnumValue} from '@/components/shared/EnumValue'
import {BadgeVariant} from '@/components/ui/badge'

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
export const ConstraintType = {
    CEL: 'DSL',
    NATIVE_CODE: 'NATIVE_CODE',
} as const;
export type ConstraintType = typeof ConstraintType[keyof typeof ConstraintType];

export const ConstraintSeverity = {
    INFO: 'INFO',
    WARN: 'WARN',
    ERROR: 'ERROR',
    CRITICAL: 'CRITICAL',
} as const;
export type ConstraintSeverity = typeof ConstraintSeverity[keyof typeof ConstraintSeverity];

export const ConstraintEvalStatus = {
    STALE: 'STALE',
} as const;
export type ConstraintEvalStatus = typeof ConstraintEvalStatus[keyof typeof ConstraintEvalStatus];
/* eslint-enable i18next/no-literal-string */

export interface Constraint {
    id: UUID;
    name: string;
    description: string | null;
    type: ConstraintType;
    expression: string | null;
    constraintCode: string | null;
    severity: ConstraintSeverity;
    scheduleCron: string | null;
    messageTemplate: string | null;
    enabled: boolean;
    evalStatus: ConstraintEvalStatus | null;
    ownerId: UUID | null;
    lastReviewedAt: Instant | null;
    lastReviewedById: UUID | null;
    version: number;
    createdAt: Instant;
    updatedAt: Instant;
}

export interface ConstraintSummary {
    id: UUID;
    name: string;
    type: ConstraintType;
    severity: ConstraintSeverity;
    enabled: boolean;
    evalStatus: ConstraintEvalStatus | null;
    openViolationsCount: number;
    lastReviewedAt: Instant | null;
    createdAt: Instant;
    updatedAt: Instant;
}

export interface CreateConstraintRequest {
    name: string;
    description?: string | null;
    type: ConstraintType;
    expression?: string | null;
    constraintCode?: string | null;
    severity: ConstraintSeverity;
    messageTemplate?: string | null;
}

export interface UpdateConstraintRequest extends CreateConstraintRequest {
    version: number;
}

/* eslint-disable i18next/no-literal-string -- field-name metamodel constants */
export const ConstraintSummaryResponseFields = {
    id: 'id',
    name: 'name',
    type: 'type',
    severity: 'severity',
    enabled: 'enabled',
    evalStatus: 'evalStatus',
    openViolationsCount: 'openViolationsCount',
    lastReviewedAt: 'lastReviewedAt',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const;
/* eslint-enable i18next/no-literal-string */

export class ConstraintSeverityBadge extends EnumValue {
    static readonly INFO = new ConstraintSeverityBadge(ConstraintSeverity.INFO, BadgeVariant.OUTLINE)
    static readonly WARN = new ConstraintSeverityBadge(ConstraintSeverity.WARN, BadgeVariant.WARNING)
    static readonly ERROR = new ConstraintSeverityBadge(ConstraintSeverity.ERROR, BadgeVariant.DESTRUCTIVE)
    static readonly CRITICAL = new ConstraintSeverityBadge(ConstraintSeverity.CRITICAL, BadgeVariant.DESTRUCTIVE)

    static readonly values = [
        ConstraintSeverityBadge.INFO,
        ConstraintSeverityBadge.WARN,
        ConstraintSeverityBadge.ERROR,
        ConstraintSeverityBadge.CRITICAL,
    ]

    static fromCode(code: string): ConstraintSeverityBadge {
        return ConstraintSeverityBadge.values.find((v) => v.code === code) ?? ConstraintSeverityBadge.INFO
    }
}
